import { useEffect, useState } from "react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { Copy, Check, ArrowLeft, ArrowRight, Warning, CheckCircle, HardDrives, ShieldCheck } from "@phosphor-icons/react";

const STEPS = ["Carrier", "Account Entry", "Dialplan", "Complete"];

function CodeBlock({ text, onCopy, copied, testid }) {
  return (
    <div className="relative">
      <pre data-testid={testid}
        className="text-xs leading-relaxed bg-muted/60 border border-border rounded-lg p-4 overflow-x-auto max-h-[46vh] whitespace-pre font-mono">
        {text}
      </pre>
      <Button size="sm" variant={copied ? "default" : "outline"} onClick={onCopy}
        data-testid={`${testid}-copy`}
        className="absolute top-2 right-2 rounded-lg h-8">
        {copied ? <><Check className="w-4 h-4 mr-1" /> Copied</> : <><Copy className="w-4 h-4 mr-1" /> Copy</>}
      </Button>
    </div>
  );
}

function ErrorNote({ title, body, testid }) {
  return (
    <div data-testid={testid} className="flex gap-3 rounded-lg border border-amber-400/40 bg-amber-400/10 p-4">
      <Warning className="w-5 h-5 text-amber-500 shrink-0 mt-0.5" weight="fill" />
      <div className="text-sm">
        <p className="font-medium text-amber-700 dark:text-amber-400">{title}</p>
        <p className="text-muted-foreground mt-0.5 whitespace-pre-line">{body}</p>
      </div>
    </div>
  );
}

export default function AsteriskWizard({ campaign, open, onClose }) {
  const [cfg, setCfg] = useState(null);
  const [loading, setLoading] = useState(true);
  const [step, setStep] = useState(0);
  const [copied, setCopied] = useState("");

  useEffect(() => {
    if (!campaign || !open) return;
    setStep(0); setCopied(""); setLoading(true); setCfg(null);
    api.get(`/campaigns/${campaign.id}/asterisk-config`)
      .then(({ data }) => setCfg(data))
      .catch((e) => { toast.error(formatApiError(e.response?.data?.detail)); onClose(); })
      .finally(() => setLoading(false));
  }, [campaign, open]); // eslint-disable-line react-hooks/exhaustive-deps

  const copy = async (text, key) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(key);
      setTimeout(() => setCopied((c) => (c === key ? "" : c)), 2000);
    } catch { toast.error("Copy failed — select the text and copy manually."); }
  };

  const noTelephony = cfg?.errors?.includes("no_telephony");
  const noPrefix = cfg?.errors?.includes("no_prefix");
  const canNextFromAccount = !!cfg?.account_entry; // blocked if no telephony

  const firewallIps = [
    ...(cfg?.telephony_server?.ip ? [{ ip: cfg.telephony_server.ip, role: "Telephony" }] : []),
    ...((cfg?.media_servers || []).filter((m) => m?.ip).map((m) => ({ ip: m.ip, role: "Media" }))),
  ];

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-2xl" data-testid="asterisk-wizard">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <HardDrives className="w-5 h-5 text-primary" weight="duotone" /> Asterisk Server Setup
          </DialogTitle>
          <DialogDescription>
            {campaign?.name} · prefix {cfg?.campaign_prefix || "—"} — configure your VICIdial carrier for the AI Bot.
          </DialogDescription>
        </DialogHeader>

        {/* Progress */}
        <div className="flex items-center gap-1.5 text-xs" data-testid="asterisk-progress">
          {STEPS.map((s, i) => (
            <div key={s} className="flex items-center gap-1.5">
              <span className={`px-2 py-1 rounded-full font-medium ${i === step ? "bg-primary text-primary-foreground" : i < step ? "bg-primary/15 text-primary" : "bg-muted text-muted-foreground"}`}>
                {i + 1}. {s}
              </span>
              {i < STEPS.length - 1 && <ArrowRight className="w-3 h-3 text-muted-foreground" />}
            </div>
          ))}
        </div>

        <div className="py-2 min-h-[220px]">
          {loading ? <p className="text-sm text-muted-foreground">Loading configuration…</p> : (
            <>
              {/* Step 1 — Carrier */}
              {step === 0 && (
                <div className="space-y-3 text-sm" data-testid="asterisk-step-carrier">
                  <p className="text-muted-foreground">In your VICIdial dialer, create a carrier that points to your assigned Bot infrastructure:</p>
                  <ol className="list-decimal ml-5 space-y-1.5">
                    <li>Open <span className="font-medium">VICIdial Admin</span>.</li>
                    <li>Go to <span className="font-medium">Carriers</span>.</li>
                    <li>Click <span className="font-medium">“Add A New Carrier”</span>.</li>
                    <li>Enter a <span className="font-medium">Carrier Name</span> and <span className="font-medium">Description</span> as you like.</li>
                    <li>On the next step, paste the generated config into the <span className="font-medium">Carrier Account Entry</span> field.</li>
                  </ol>

                  {/* VICIdial Firewall entries */}
                  {firewallIps.length > 0 && (
                    <div data-testid="asterisk-firewall" className="rounded-lg border border-sky-400/40 bg-sky-400/10 p-4 space-y-2">
                      <div className="flex items-center gap-2">
                        <ShieldCheck className="w-4 h-4 text-sky-500 shrink-0" weight="fill" />
                        <p className="font-medium text-sky-700 dark:text-sky-400">VICIdial Firewall entries</p>
                      </div>
                      <p className="text-muted-foreground text-xs">
                        If you run a firewall on your VICIdial server, you may need to whitelist ALL of the
                        assigned Telephony &amp; Media server IPs below so SIP/RTP traffic to and from the AI Bot is allowed.
                      </p>
                      <ul className="space-y-1" data-testid="asterisk-firewall-list">
                        {firewallIps.map((f) => (
                          <li key={f.ip} className="flex items-center justify-between gap-2 rounded-md bg-background/60 border border-border px-3 py-1.5">
                            <span className="font-mono text-foreground text-xs" data-testid={`asterisk-firewall-ip-${f.ip}`}>{f.ip}</span>
                            <span className="text-[10px] uppercase tracking-wide px-1.5 py-0.5 rounded-full bg-muted text-muted-foreground">{f.role}</span>
                          </li>
                        ))}
                      </ul>
                      <Button size="sm" variant={copied === "firewall" ? "default" : "outline"} className="rounded-lg h-8"
                        data-testid="asterisk-firewall-copy"
                        onClick={() => copy(firewallIps.map((f) => f.ip).join("\n"), "firewall")}>
                        {copied === "firewall" ? <><Check className="w-4 h-4 mr-1" /> Copied</> : <><Copy className="w-4 h-4 mr-1" /> Copy all IPs</>}
                      </Button>
                    </div>
                  )}
                </div>
              )}

              {/* Step 2 — Account Entry */}
              {step === 1 && (
                <div className="space-y-3" data-testid="asterisk-step-account">
                  {noTelephony ? (
                    <ErrorNote testid="asterisk-error-telephony"
                      title="No Telephony Server is assigned to this campaign."
                      body={"Please assign a Telephony Server before generating\nthe Asterisk configuration."} />
                  ) : (
                    <>
                      <div className="text-xs text-muted-foreground flex flex-wrap gap-x-4 gap-y-1">
                        <span>Telephony: <span className="font-mono text-foreground">{cfg?.telephony_server?.ip}</span></span>
                        <span>Media servers: <span className="font-mono text-foreground">{cfg?.media_servers?.length || 0}</span></span>
                      </div>
                      <p className="text-sm text-muted-foreground">Paste this into the VICIdial <span className="font-medium">Carrier Account Entry</span> field:</p>
                      <CodeBlock testid="asterisk-account-entry" text={cfg?.account_entry}
                        copied={copied === "account"} onCopy={() => copy(cfg.account_entry, "account")} />
                      {(cfg?.media_servers?.length || 0) === 0 && (
                        <p className="text-xs text-muted-foreground">No Media Servers are assigned — only the <span className="font-mono">{cfg?.telephony_peer || "BotTelephony"}</span> section is generated.</p>
                      )}
                    </>
                  )}
                </div>
              )}

              {/* Step 3 — Dialplan */}
              {step === 2 && (
                <div className="space-y-3" data-testid="asterisk-step-dialplan">
                  {noPrefix ? (
                    <ErrorNote testid="asterisk-error-prefix"
                      title="Campaign prefix is not configured for this campaign."
                      body={"Please configure the campaign prefix before generating\nthe dialplan."} />
                  ) : (
                    <>
                      <p className="text-sm text-muted-foreground">Add this to your dialplan (extension prefix <span className="font-mono text-foreground">{cfg?.campaign_prefix}</span>):</p>
                      <CodeBlock testid="asterisk-dialplan" text={cfg?.dialplan}
                        copied={copied === "dialplan"} onCopy={() => copy(cfg.dialplan, "dialplan")} />
                    </>
                  )}
                </div>
              )}

              {/* Step 4 — Complete */}
              {step === 3 && (
                <div className="space-y-3 text-sm" data-testid="asterisk-step-complete">
                  <div className="flex items-center gap-2 text-emerald-600">
                    <CheckCircle className="w-5 h-5" weight="fill" />
                    <span className="font-medium">You’re all set.</span>
                  </div>
                  <p className="text-muted-foreground">Save the carrier and dialplan in VICIdial, then reload the Asterisk dialplan. Calls dialed on prefix <span className="font-mono text-foreground">{cfg?.campaign_prefix}</span> will route to the AI Bot via <span className="font-mono">{cfg?.telephony_peer || "BotTelephony"}</span>.</p>
                  <ul className="list-disc ml-5 space-y-1 text-muted-foreground">
                    <li>Carrier Account Entry pasted with your Telephony{cfg?.media_servers?.length ? " + Media" : ""} peers.</li>
                    <li>Dialplan added with your campaign prefix.</li>
                  </ul>
                </div>
              )}
            </>
          )}
        </div>

        <DialogFooter className="gap-2 sm:justify-between">
          <Button variant="ghost" onClick={onClose} data-testid="asterisk-close" className="rounded-lg">Close</Button>
          <div className="flex gap-2">
            {step > 0 && (
              <Button variant="outline" onClick={() => setStep((s) => s - 1)} data-testid="asterisk-back" className="rounded-lg">
                <ArrowLeft className="w-4 h-4 mr-1" /> Back
              </Button>
            )}
            {step < STEPS.length - 1 && (
              <Button onClick={() => setStep((s) => s + 1)} data-testid="asterisk-next"
                disabled={loading || (step === 1 && !canNextFromAccount)} className="rounded-lg">
                Next <ArrowRight className="w-4 h-4 ml-1" />
              </Button>
            )}
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
